import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { CATEGORY_BY_ID } from '@/registry/categories';
import { TOOLS_BY_CATEGORY } from '@/registry';
import { ToolCard } from '@/components/ToolCard';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';

export function CategoryView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const category = id ? CATEGORY_BY_ID[id] : null;
  const tools = id ? TOOLS_BY_CATEGORY[id] ?? [] : [];

  if (!category) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <EmptyState
          title="Category not found"
          description="The category you're looking for doesn't exist."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              <ArrowLeft size={14} />
              Back to home
            </Button>
          }
        />
      </div>
    );
  }

  const Icon = category.icon;

  return (
    <div>
      <div className="relative overflow-hidden border-b border-border">
        <div
          className={cn(
            'absolute -top-32 left-1/4 size-96 rounded-full blur-3xl opacity-30 bg-gradient-to-br',
            category.gradient,
          )}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-start gap-4"
          >
            <div
              className={cn(
                'size-12 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-md',
                category.gradient,
              )}
            >
              <Icon size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">{category.name}</h1>
              <p className="text-fg-muted mt-1.5">{category.description}</p>
              <div className="text-xs text-fg-subtle mt-2">{tools.length} utilities</div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
        {tools.length === 0 ? (
          <EmptyState
            title="More utilities coming soon"
            description="We're actively adding tools to this category."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {tools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
